import React, { useState, useContext } from 'react'
import styled from 'styled-components'
import { IconDown } from './GroupDatabaseTable'
import dateFormat from './formatDate'
import ListDatabase from './ListDatabase'
import NewDatabaseGroupDialog from './NewDatabaseGroupDialog'
import { deleteGroupModel } from '../../api/groupDatabases'
import MultipleUploadsDialog from './MultipleUploadsDialog'
import Dropzone from 'react-dropzone'
import { DatabaseModel, GroupModel } from './interface/Group'
import DeleteConfirmationModal from './alerts/DeleteConfirmationModal'
import { UpdateContext } from '../GroupDatabase'

interface Props {
    groupModel: GroupModel
    databaseModels: DatabaseModel[]
}
export default function RowGroupTable({ groupModel }: Props): JSX.Element {
    const [showTables, setShowTables] = useState<boolean>(false)
    const [showEditGroupModal, setShowEditGroupModal] = useState<boolean>(false)
    const [showMultipleUploadsDialog, setShowMultipleUploadsDialog] = useState(false)
    const [files, setFiles] = useState<File[]>([])
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)

    const handleEditGroupModel = () => {
        setShowEditGroupModal(true)
    }

    const handleDeleteGroupModel = event => {
        event.preventDefault()
        deleteGroupModel(groupModel).then(() => {
            updateData()
        })
        setShowDeleteConfirmationModal(false)
    }
    const handleCancel = () => {
        setShowDeleteConfirmationModal(false)
    }

    const { updateData } = useContext(UpdateContext)

    return (
        <GroupTable showTables={showTables}>
            {showMultipleUploadsDialog ? (
                <MultipleUploadsDialog
                    showDialog={show => setShowMultipleUploadsDialog(show)}
                    files={files}
                    groupModel={groupModel}
                />
            ) : null}
            <NewDatabaseGroupDialog
                mode={'edit'}
                show={showEditGroupModal}
                groupModel={groupModel}
                setShowNewGroupModal={setShowEditGroupModal}
            />
            <DeleteConfirmationModal
                handleCancel={handleCancel}
                show={showDeleteConfirmationModal}
                handleDelete={handleDeleteGroupModel}
            />
            <div className="main-container" data-cy={groupModel.name + '-mainContainer'}>
                <div className={`header-div`}>
                    <div className="icon-drop">
                        <IconDown onClick={() => setShowTables(!showTables)} data-cy={groupModel.name + '-dropIcon'}>
                            <i className={`fas fa-sort-${showTables ? 'up' : 'down'} drop-down`} />
                        </IconDown>
                    </div>
                    <div className="div-center">{groupModel.name}</div>
                    <div className="div-center">
                        {groupModel.templates.length < 10
                            ? '0' + groupModel.templates.length
                            : groupModel.templates.length}
                    </div>
                    <div className="div-center">{dateFormat(Number(groupModel.timestamp))}</div>
                    <div data-cy={groupModel.name + '-options'} className="div-center modal-icons">
                        <div className="icon-upload">
                            <Container>
                                <Dropzone
                                    className="__dropComponent"
                                    onDrop={files => {
                                        setFiles([...files])
                                        setShowMultipleUploadsDialog(true)
                                    }}
                                    data-cy="drag-and-drop"
                                >
                                    {({ getRootProps, getInputProps }) => {
                                        return (
                                            <span {...getRootProps()}>
                                                 <input {...getInputProps()} />
                                                <Icon>
                                                    <span>
                                                        <i className="far fa-upload" />
                                                        Upload Data
                                                    </span>
                                                </Icon>
                                                                                                
                                            </span>
                                        )
                                    }}
                                </Dropzone>
                            </Container>
                        </div>
                        <div className="div-center group-row-actions">
                            <span>
                                <i
                                    data-cy={groupModel.name + '-edit'}
                                    onClick={event => {
                                        handleEditGroupModel()
                                    }}
                                    className="far fa-edit edit-icon"
                                />
                                <i
                                    data-cy={groupModel.name + '-remove'}
                                    onClick={() => {
                                        setShowDeleteConfirmationModal(true)
                                    }}
                                    className="far fa-trash-alt trash-icon"
                                />
                            </span>
                        </div>
                    </div>
                </div>
                <div className="list-databases">
                    {showTables ? <ListDatabase group={groupModel.templates} /> : null}
                </div>
            </div>
        </GroupTable>
    )
}

const GroupTable = styled.div<{
    showTables: boolean
}>`
    .main-container {
        border: ${({ showTables }) => (showTables ? '1px solid var(--gray-400)' : 'none')};
        border-radius: 5px;
        width: 80%;
        min-width: 680px;
        margin: 0 auto;
        box-shadow: ${({ showTables }) =>
            showTables ? '3px 3px 2px var(--gray-100), -3px 3px 2px var(--gray-100)' : '0'};

        :hover {
            background: ${({ showTables }) => (showTables ? 'var(--gray-0)' : 'var(--blue-200)')};
            transition: 0.3s;
            .group-row-actions {
                display: block;
            }
        }

        .header-div {
            height: 64px;
            display: grid;
            grid-template-columns: 4% 25% 22% 22% 27%;
            margin: 0 auto;
            font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
            font-size: 12px;
            width: 100%;
            color: var(--gray-700);
            font-style: normal;
            font-weight: 600;
            border-top: ${({ showTables }) => (showTables ? 'none' : '1px solid var(--gray-400)')};
        }
    }

    .div-center {
        font-family: Source Sans Pro, Open Sans Pro, Arial, Helvetica, sans-serif;
        font-size: 12px;
        line-height: 16px;
        color: var(--gray-800);
        font-style: normal;
        font-weight: 400;
        display: grid;
        text-align: left;
        align-content: space-evenly;
        padding-left: 10px;
    }

    .modal-icons {
        display: flex;
        text-align: center;
        align-items: center;
        justify-content: space-between;
        min-width: 180px;
        padding-left: 0;

        .icon-upload {
            min-width: 125px;
        }

        .group-row-actions {
            align-items: baseline;
            max-width: 80px;
        }
    }

    .icon-drop {
        padding-top: 20px;
        padding-right: 5px;
        width: 50px;
        text-align: center;
        justify-content: center;
    }

    .list-databases {
        width: 95%;
        margin: 0 auto;
    }

    .icon-upload {
        font-size: 11px;
        line-height: 16px;
        text-align: left;
        justify-content: left;
    }

    .group-row-actions {
        display: ${({ showTables }) => (showTables ? 'block' : 'none')};
        cursor: pointer;
        padding-right: 2px;
    }
    .edit-icon {
        text-align: left;
        justify-content: left;
        font-weight: 400;
        font-style: normal;
        font-size: 12px;
        line-height: 16px;
        color: var(--blue-500);
    }
    .trash-icon {
        padding-left: 8px;
        font-weight: 400;
        font-style: normal;
        font-size: 12px;
        line-height: 16px;
        color: var(--red-500);
    }

    .edit-icon:hover {
        color: var(--blue-400);
    }

    .trash-icon:hover {
        color: var(--red-300);
    }
    span {
        font-size: 13px;
        line-height: 16px;
        padding-left: 8px;
        padding-right: 5px;
    }
`

const Container = styled.div`
    height: 100%;
    /* width: 20px; */
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    /* padding: 0 1.4rem; */
    cursor: pointer;

    .__dropComponent {
        height: 85%;
        width: 85%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`
const Icon = styled.i`
    font-family: Font Awesome 5 Pro;
    font-style: normal;
    font-weight: 400;
    font-size: 11px;
    line-height: 16px;
    color: var(--blue-500);
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;

    &:hover {
        opacity: 0.6;
    }

    span {
        font-family: Source Sans Pro, Arial, Helvetica, sans-serif;
        font-style: normal;
        font-weight: 600;
        font-size: 13px;
        line-height: 16px;
        display: block;
        padding: 0 0.4rem;
        white-space: nowrap;
        color: var(--blue-500);
        i {
            padding-right: 10px;
        }
    }
`
